/*
 **	Pressure Sparkline Chart Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React from 'react'
import { OCAColors } from '../../theme/colors'

interface DataPoint {
  timestamp: number
  value: number
  alarm_state?: number
}

interface PressureSparklineProps {
  data: DataPoint[]
  width?: number
  height?: number
  warningThreshold?: number
  alarmThreshold?: number
  showThresholds?: boolean
  color?: string
  onClick?: () => void
}

export const PressureSparkline: React.FC<PressureSparklineProps> = ({
  data,
  width = 120,
  height = 40,
  warningThreshold,
  alarmThreshold,
  showThresholds = true,
  color = OCAColors.primary,
  onClick,
}) => {
  if (!data || data.length < 2) {
    return (
      <div
        style={{
          width: `${width}px`,
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: OCAColors.tertiaryLabel,
          fontSize: '10px',
        }}
      >
        No data
      </div>
    )
  }

  // Find min and max values for scaling
  const values = data.map(d => d.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)

  // Add padding to the range (10% on each side)
  const range = maxValue - minValue
  const padding = range * 0.1 || 1 // Avoid zero padding
  const yMin = minValue - padding
  const yMax = maxValue + padding
  const yRange = yMax - yMin

  // Generate SVG path for the line
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((d.value - yMin) / yRange) * height
    return { x, y, value: d.value, alarm_state: d.alarm_state }
  })

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`)
    .join(' ')

  // Generate area fill path
  const areaPath = `${linePath} L ${width},${height} L 0,${height} Z`

  // Calculate threshold y positions
  const getThresholdY = (threshold: number) => {
    if (threshold < yMin || threshold > yMax) return null
    return height - ((threshold - yMin) / yRange) * height
  }

  const warningY = warningThreshold ? getThresholdY(warningThreshold) : null
  const alarmY = alarmThreshold ? getThresholdY(alarmThreshold) : null

  // Determine line color based on current value and thresholds
  const currentValue = data[data.length - 1].value
  let lineColor = color
  if (alarmThreshold && currentValue >= alarmThreshold) {
    lineColor = OCAColors.danger
  } else if (warningThreshold && currentValue >= warningThreshold) {
    lineColor = OCAColors.warning
  } else {
    lineColor = OCAColors.success
  }

  return (
    <div
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        display: 'inline-block',
      }}
    >
      <svg
        width={width}
        height={height}
        style={{
          display: 'block',
          overflow: 'visible',
        }}
      >
        {/* Gradient fill */}
        <defs>
          <linearGradient id={`gradient-${lineColor}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.2" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Area fill */}
        <path
          d={areaPath}
          fill={`url(#gradient-${lineColor})`}
          strokeWidth="0"
        />

        {/* Warning threshold line */}
        {showThresholds && warningY !== null && (
          <line
            x1="0"
            y1={warningY}
            x2={width}
            y2={warningY}
            stroke={OCAColors.warning}
            strokeWidth="1"
            strokeDasharray="2,2"
            opacity="0.5"
          />
        )}

        {/* Alarm threshold line */}
        {showThresholds && alarmY !== null && (
          <line
            x1="0"
            y1={alarmY}
            x2={width}
            y2={alarmY}
            stroke={OCAColors.danger}
            strokeWidth="1"
            strokeDasharray="2,2"
            opacity="0.5"
          />
        )}

        {/* Main line */}
        <path
          d={linePath}
          fill="none"
          stroke={lineColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* End point indicator */}
        <circle
          cx={points[points.length - 1].x}
          cy={points[points.length - 1].y}
          r="3"
          fill={lineColor}
          stroke="white"
          strokeWidth="1.5"
        />

        {/* Alarm state indicators */}
        {points.map((p, i) => {
          const alarmState = p.alarm_state
          // Only show dots for alarm (3) or warning (2) states
          if (alarmState === 3) {
            return (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="2"
                fill={OCAColors.danger}
                opacity="0.8"
              />
            )
          } else if (alarmState === 2) {
            return (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="2"
                fill={OCAColors.warning}
                opacity="0.8"
              />
            )
          }
          return null
        })}
      </svg>
    </div>
  )
}
